import Swiper from 'swiper'
import { A11y, Keyboard, Mousewheel, Navigation } from 'swiper/modules'
import { onPage } from '../app/lifecycle'
import { getState } from '../app/store'

/**
 * FAQ slider (template-parts/blocks/block-faq-slider.php).
 *
 * The server renders a plain question/answer list; with effects on, Swiper
 * turns it into one row of questions with the active answer opening below.
 * Easy mode keeps the list (the page is remounted when the mode toggles).
 *
 * Swiper runs on the theme's own class names (faq-slider-list/-item) and the
 * layout lives in 85_blocks.sass, keyed on the .is-slider class this module
 * sets — so no Swiper stylesheet is needed. Until then the block stays
 * hidden; should this module never run, the CSS falls back to the list.
 *
 * slidesPerView is 'auto' (slide width set in CSS): loop mode needs
 * "visible slides + looped slides" real slides, which with a fractional
 * slidesPerView of 1.6 would be five — more than the four FAQs there are.
 */
const FOCUSABLE =
  'a[href], area[href], button, input, select, textarea, iframe, summary, [tabindex], [contenteditable="true"]'

const SPEED = 650

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

function readLabels(block) {
  try {
    return JSON.parse(block.dataset.faqLabels ?? '{}')
  } catch {
    return {}
  }
}

/**
 * Collapsed answers stay in the accessibility tree (screen readers can read
 * every answer), but their links must not be reachable with Tab.
 */
function setFocusable(answer, focusable) {
  answer?.querySelectorAll(FOCUSABLE).forEach((el) => {
    el.dataset.faqTabindex ??= el.getAttribute('tabindex') ?? ''
    if (!focusable) {
      el.setAttribute('tabindex', '-1')
    } else if (el.dataset.faqTabindex === '') {
      el.removeAttribute('tabindex')
    } else {
      el.setAttribute('tabindex', el.dataset.faqTabindex)
    }
  })
}

/**
 * Gives the question a temporary focus stop (tabindex -1, gone again on blur)
 * and moves the focus there.
 */
function focusQuestion(slide) {
  const question = slide?.querySelector('[data-faq-question]')
  if (!question) return false
  question.setAttribute('tabindex', '-1')
  question.addEventListener('blur', () => question.removeAttribute('tabindex'), { once: true })
  question.focus({ preventScroll: true })
  return true
}

/**
 * Active slide opens its answer; slides left/right of it align their question
 * towards it. Returns true when it had to move the focus.
 */
function syncSlides(swiper) {
  const { activeIndex, slides } = swiper
  const focused = document.activeElement
  let focusLeftBehind = false
  slides.forEach((slide, index) => {
    const isActive = index === activeIndex
    slide.classList.toggle('is-active', isActive)
    slide.classList.toggle('is-prev', index < activeIndex)
    slide.classList.toggle('is-next', index > activeIndex)
    setFocusable(slide.querySelector('[data-faq-answer]'), isActive)
    if (!isActive && focused && slide.contains(focused)) focusLeftBehind = true
  })
  // E.g. Tab to the phone link, then an arrow key: the link's answer has just
  // collapsed, so the focus follows to the new question instead of staying on
  // an invisible link.
  return focusLeftBehind && focusQuestion(slides[activeIndex])
}

/**
 * Without loop (two or three FAQs) Swiper sets `disabled` on the prev/next
 * <button> at either end. A focused button that becomes disabled drops the
 * focus to <body>, so a keyboard user pressing "next" would lose their place
 * at the last question: the focus moves on to the opposite button, or to the
 * active question should that one be unusable too (same hand-off as
 * menuSlider.js). Runs after Swiper's own toEdge handler, which does the
 * disabling (modules subscribe before the `on` params); browsers only fix up
 * the focus at the next rendering step, so the disabled button still reports
 * as focused here. Mouse and touch users never focus these buttons.
 */
function keepNavFocus(swiper, prevEl, nextEl) {
  const focused = document.activeElement
  if (!focused?.disabled || (focused !== prevEl && focused !== nextEl)) return
  const atEnd = focused === nextEl
  const other = atEnd ? prevEl : nextEl
  if (other && !other.disabled && !other.hidden) {
    other.focus({ preventScroll: true })
  } else {
    // toEdge comes before Swiper updates activeIndex: the edge slide is the new active one.
    focusQuestion(atEnd ? swiper.slides.at(-1) : swiper.slides[0])
  }
}

/** Undo what Swiper's A11y module wrote, so the list view is plain again. */
function resetAttributes(viewport, items) {
  ;['role', 'aria-label', 'aria-roledescription'].forEach((name) => {
    viewport.removeAttribute(name)
    items.forEach((item) => item.removeAttribute(name))
  })
  viewport.firstElementChild?.removeAttribute('aria-live')
  viewport.firstElementChild?.removeAttribute('id')
}

function mountSlider(block) {
  const viewport = block.querySelector('[data-faq-viewport]')
  const items = [...block.querySelectorAll('[data-faq-item]')]
  const prevEl = block.querySelector('[data-faq-prev]')
  const nextEl = block.querySelector('[data-faq-next]')
  const status = block.querySelector('[data-faq-status]')

  if (!viewport) return null

  // .is-slider switches the CSS to the slider layout; .is-mounting holds every
  // transition while it is being built, so the neighbour questions don't glide
  // into their places on each mount. settle() commits the finished layout
  // (the forced layout runs the style change with the transitions still off)
  // and turns them on for the real slide changes.
  block.classList.add('is-slider', 'is-mounting')
  const settle = () => {
    block.getBoundingClientRect()
    block.classList.remove('is-mounting')
  }

  // A single FAQ needs no Swiper — the CSS centres it, open.
  if (items.length < 2) {
    settle()
    return () => block.classList.remove('is-slider')
  }

  const labels = readLabels(block)
  const reducedMotion = prefersReducedMotion()
  prevEl?.removeAttribute('hidden')
  nextEl?.removeAttribute('hidden')

  // Swiper fires slideChange while it initialises and when loopFix re-sorts
  // the slides around the same question — the status only announces a
  // question that really changed. null until the slider is ready.
  let shownIndex = null

  const swiper = new Swiper(viewport, {
    modules: [A11y, Keyboard, Mousewheel, Navigation],
    wrapperClass: 'faq-slider-list',
    slideClass: 'faq-slider-item',
    slidesPerView: 'auto',
    centeredSlides: true,
    // With both neighbours peeking in, three slides are all on screen at
    // once: the one leaving on the left would have to enter on the right at
    // the same time, so a loop of three leaves the right side empty after
    // every step forward. Two or three FAQs slide without looping.
    loop: items.length >= 4,
    speed: reducedMotion ? 0 : SPEED,
    grabCursor: true,
    slideToClickedSlide: true,
    keyboard: { enabled: true, onlyInViewport: true, pageUpDown: false },
    mousewheel: {
      enabled: true,
      // One slide per wheel gesture: ignore tiny deltas and wait for the slide to settle.
      thresholdDelta: 12,
      thresholdTime: reducedMotion ? 400 : SPEED,
    },
    navigation: { prevEl, nextEl, addIcons: false, disabledClass: 'is-disabled' },
    a11y: {
      containerRole: 'region',
      containerMessage: labels.container,
      containerRoleDescriptionMessage: labels.containerRole,
      itemRoleDescriptionMessage: labels.item,
      slideLabelMessage: labels.itemLabel,
      prevSlideMessage: labels.prev,
      nextSlideMessage: labels.next,
      firstSlideMessage: labels.first,
      lastSlideMessage: labels.last,
      notificationClass: 'faq-slider-notification',
      // Loop mode re-inserts slides into the DOM, which a live wrapper would
      // read out; the active question is announced via [data-faq-status].
      wrapperLiveRegion: false,
    },
    on: {
      afterInit: syncSlides,
      loopFix: syncSlides,
      toEdge: (instance) => keepNavFocus(instance, prevEl, nextEl),
      slideChange(instance) {
        const movedFocus = syncSlides(instance)
        if (shownIndex === null || instance.realIndex === shownIndex) return
        shownIndex = instance.realIndex
        if (!status) return
        const question = instance.slides[instance.activeIndex]?.querySelector('[data-faq-question]')
        // A moved focus already makes screen readers read the new question.
        status.textContent = movedFocus ? '' : (question?.textContent.trim() ?? '')
      },
    },
  })
  settle()
  shownIndex = swiper.realIndex

  // A vertical wheel flips the questions while the page has nothing to
  // scroll; once a long answer makes the page taller than the screen, the
  // vertical wheel scrolls the page again and only a horizontal one slides.
  const onWheel = () => {
    swiper.params.mousewheel.forceToAxis =
      document.documentElement.scrollHeight > window.innerHeight + 1
  }
  block.addEventListener('wheel', onWheel, { capture: true, passive: true })

  return () => {
    block.removeEventListener('wheel', onWheel, { capture: true })
    swiper.destroy(true, true)
    resetAttributes(viewport, items)
    items.forEach((item, index) => {
      item.classList.remove('is-prev', 'is-next')
      item.classList.toggle('is-active', index === 0)
      setFocusable(item.querySelector('[data-faq-answer]'), true)
    })
    block.querySelectorAll('[data-faq-question]').forEach((question) => {
      question.removeAttribute('tabindex')
    })
    prevEl?.setAttribute('hidden', '')
    nextEl?.setAttribute('hidden', '')
    if (status) status.textContent = ''
    block.classList.remove('is-slider', 'is-mounting')
  }
}

onPage((root) => {
  // Easy mode: the stacked list from the server is the whole view.
  if (getState().easyMode) return undefined

  const cleanups = [...root.querySelectorAll('[data-faq-slider]')]
    .map((block) => mountSlider(block))
    .filter(Boolean)

  return () => cleanups.forEach((cleanup) => cleanup())
})
